import {
  Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren,
  ViewEncapsulation
} from '@angular/core';
import {AutoGroupDropdown} from '../../../../../../models/group-settings';
import {ReferenceDataService} from '../../../../../services/reference-data/reference-data.service';
import {AutoGroupFilterList, GenderEnum} from '../../../../../app.constants';
import {TranslateService} from '@ngx-translate/core';
import {ErrorTemplate, ValidationError} from '../../../../../../models/ErrorTemplate';
import {
  AutoGroupFilter,
  AutoGroupFilterConfig,
  AutoGroupFilterEnum
} from '../../../../../../models/auto-group/auto-group-setting';
import {AppSettings} from '../../../../../utils/app-settings';
import {AutoGroupFilterBoxComponent} from '../../auto-group-filter-box/auto-group-filter-box.component';

@Component({
  selector: 'auto-group-filter-item',
  templateUrl: './auto-group-filter-item.component.html',
  styleUrls: ['./auto-group-filter-item.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AutoGroupFilterItemComponent implements OnInit {
  @ViewChildren(AutoGroupFilterBoxComponent) filterBoxComponents: QueryList<AutoGroupFilterBoxComponent>;
  @Input() supportedFilterOptions: Array<AutoGroupDropdown> = [];
  @Input() autoGroupFilter: AutoGroupFilter = new AutoGroupFilter();
  @Output() deleteGroupFilterEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() changeGroupFilterEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() changePlatformFilterEvent: EventEmitter<any> = new EventEmitter<any>();

  isMultipleSelect: boolean;
  errorMessage: string;
  warningMessage: string;
  hasError: boolean | false;
  // filter selections
  private countries: AutoGroupDropdown[] = [];
  private genders: AutoGroupDropdown[] = [];
  private audienceGenders: AutoGroupDropdown[] = [];
  private activePlatforms: AutoGroupDropdown[] = [];
  public firstFilterValues: AutoGroupDropdown[] = [];
  public secondFilterValues: AutoGroupDropdown[] = [];
  public operatorList: AutoGroupDropdown[] = [];

  constructor(private referenceDataService: ReferenceDataService,
              private translate: TranslateService) {
    this.countries = referenceDataService.countries.map(c => ({
      id: c.id,
      name: c.name,
      value: c.name
    }));
    const maleObserver = translate.get('new_group_settings.auto_group_filters.male');
    maleObserver.subscribe((male) => {
      const female = translate.instant('new_group_settings.auto_group_filters.female');
      const onlineMedia = translate.instant('new_group_settings.auto_group_filters.online_media');
      this.genders = [
        {
          name: female,
          id: GenderEnum.FEMALE,
          value: female
        },
        {
          name: male,
          id: GenderEnum.MALE,
          value: male
        },
        {
          name: onlineMedia,
          id: GenderEnum.ONLINE_MEDIA,
          value: onlineMedia
        }
      ];

      this.audienceGenders = [
        {
          name: female,
          id: GenderEnum.FEMALE,
          value: female
        },
        {
          name: male,
          id: GenderEnum.MALE,
          value: male
        }
      ];
    });
    this.activePlatforms = this.referenceDataService.platforms.filter(platform => AppSettings.getActivePlatformList().indexOf(platform.name.toLowerCase()) >= 0)
      .map(p => ({
        id: p.id,
        name: p.name,
        value: p.name
      }));
  }

  ngOnInit() {
    if (this.autoGroupFilter.type) {
      const filterConfig = AutoGroupFilterConfig[this.autoGroupFilter.type];
      this.isMultipleSelect = filterConfig && filterConfig.multiple;
      this.firstFilterValues = this.getFirstFilterValues(this.autoGroupFilter.type);
      this.secondFilterValues = this.getSecondFilterValues(this.autoGroupFilter.type);
    }
  }

  selectFilter() {
    this.autoGroupFilter.comparison = null;
    this.changeGroupFilterEvent.emit(this.autoGroupFilter);
    const filterConfig = AutoGroupFilterConfig[this.autoGroupFilter.type];
    this.isMultipleSelect = filterConfig && filterConfig.multiple;
    if (this.isMultipleSelect) {
      this.autoGroupFilter.secondValue = [];
    } else {
      this.autoGroupFilter.secondValue = null;
    }
    this.firstFilterValues = this.getFirstFilterValues(this.autoGroupFilter.type);
    this.secondFilterValues = this.getSecondFilterValues(this.autoGroupFilter.type);
    this.clearAllFieldErrors();
  }

  selectComparison(evt) {
    this.changeGroupFilterEvent.emit(this.autoGroupFilter);
    this.checkAndClearPreviousErrors();
  }

  deleteGroupFilter() {
    this.deleteGroupFilterEvent.emit(this.autoGroupFilter);
  }

  private getSecondFilterValues(filterType) {
    switch (filterType) {
      case AutoGroupFilterEnum.COUNTRY:
      case AutoGroupFilterEnum.AUDIENCE_COUNTRY:
        return this.countries;
      case AutoGroupFilterEnum.INFLUENCER_GENDER:
        return this.genders;
      case AutoGroupFilterEnum.AUDIENCE_GENDER:
        return this.audienceGenders;
      case AutoGroupFilterEnum.AUDIENCE_AGE:
        return AutoGroupFilterList.audienceAgeList.map(item => {
          const ageOption = this.translate.instant('new_group_settings.auto_group_filters.audiencer_age_list', {audienceAgeValue: item.value});
          return {
            id: item.id,
            name: ageOption,
            value: ageOption
          };
        });
      case AutoGroupFilterEnum.AVG_REACH_PER_POST:
      case AutoGroupFilterEnum.AVG_ENGAGEMENT_PER_POST:
      case AutoGroupFilterEnum.AVG_INTERACTION_PER_POST:
      case AutoGroupFilterEnum.FOLLOWERS:
        return AutoGroupFilterList.autoGroupValueList.map(item => ({
          id: item.id,
          name: item.name,
          value: item.name
        }));
      case AutoGroupFilterEnum.PLATFORM:
        return this.activePlatforms;
      default:
        return [];
    }
  }

  public updateOperatorList(operatorList) {
    this.operatorList = operatorList;
    const filterConfig = AutoGroupFilterConfig[this.autoGroupFilter.type];
    if (filterConfig.comparison.length === 1 && this.operatorList.length > 0) {
      this.autoGroupFilter.comparison = this.operatorList[0].id;
    }
  }

  get hasMultipleValues() {
    const filterConfig = AutoGroupFilterConfig[this.autoGroupFilter.type];
    return filterConfig && filterConfig.numOfCol === AutoGroupFilterList.multipleValueColumn;
  }

  get hasMultipleComparison() {
    const filterConfig = AutoGroupFilterConfig[this.autoGroupFilter.type];
    return this.operatorList.length > 0 && filterConfig && filterConfig.comparison.length > 1;
  }

  get hasSingleComparison() {
    const filterConfig = AutoGroupFilterConfig[this.autoGroupFilter.type];
    return this.operatorList.length > 0 && filterConfig && filterConfig.comparison.length === 1;
  }

  private getFirstFilterValues(filterType) {
    if (!filterType) {
      return [];
    }
    const filterConfig = AutoGroupFilterConfig[filterType];
    if (!filterConfig || filterConfig.numOfCol === AutoGroupFilterList.singleValueColumn) {
      return [];
    }
    return AutoGroupFilterList.audienceOperatorList.map(item => ({
      id: item.id,
      name: this.translate.instant('new_group_settings.auto_group_filters.audiencer_operator_dropdown', {operatorItem: item.value}),
      value: this.translate.instant('new_group_settings.auto_group_filters.audiencer_operator_dropdown', {operatorItem: item.value})
    }));

  }

  public validateFilters(): ErrorTemplate[] {
    const errors: ErrorTemplate[] = [];
    if (!this.autoGroupFilter.type) {
      errors.push(new ValidationError(`auto_group_filter_${this.autoGroupFilter.index}`, 'Incomplete filter'));
    }
    if (!this.autoGroupFilter.comparison) {
      errors.push(new ValidationError(`filter_comparison_${this.autoGroupFilter.index}`, 'Incomplete filter'));
    }
    if (this.hasMultipleValues && !this.autoGroupFilter.firstValue) {
      errors.push(new ValidationError(`first_filter_value_${this.autoGroupFilter.index}`, 'Incomplete filter'));
    }
    if ((Array.isArray(this.autoGroupFilter.secondValue) && this.autoGroupFilter.secondValue.length === 0) || this.autoGroupFilter.secondValue === null) {
      errors.push(new ValidationError(`second_filter_value_${this.autoGroupFilter.index}`, 'Incomplete filter'));
    }
    if (errors.length > 0) {
      this.errorMessage = this.translate.instant('new_group_settings.not_complete');
    } else {
      this.errorMessage = null;
    }
    return errors;
  }

  public checkAndClearPreviousErrors() {
    if (this.errorMessage) {
      this.validateFilters();
    }
  }

  public selectSecondFilterValue(evt) {
    this.checkAndClearPreviousErrors();
    if (this.autoGroupFilter.type === AutoGroupFilterEnum.PLATFORM) {
      const selectedPlatform = evt ? evt.name : null;
      this.changePlatformFilterEvent.emit(selectedPlatform);
    }
  }

  clearAllFieldErrors() {
    this.filterBoxComponents.forEach(comp => comp.refreshErrors([[], true]));
    this.errorMessage = null;
  }

  public updateWarningMessage(selectedPlatform) {
    if (!selectedPlatform) {
      this.warningMessage = null;
    } else {
      this.warningMessage = this.translate.instant('new_group_settings.warning_platform_only', {platform: selectedPlatform});
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/main/group-details/new-group-settings/auto-group-filters/auto-group-filter-item/auto-group-filter-item.component.ts